import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    User,
    Mail,
    Phone,
    Shield,
    MapPin,
    Calendar,
    Clock,
    AlertCircle,
    CheckCircle,
    Edit2,
    Save,
    X,
    Users,
    FileText,
    Activity,
    Lock,
    Unlock,
    Trash2,
    BookOpen,
    GraduationCap,
    Link2,
    Truck,
    KeyRound,
    Copy,
    Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
    network_administrator: 'Network Administrator',
    network_manager: 'Network Manager',
    local_controller: 'Local Controller',
    deputy_local_controller: 'Deputy Local Controller',
    unit_duty_officer: 'Unit Duty Officer',
    group_administrator: 'Group Administrator',
    group_leader: 'Group Leader',
    deputy_group_leader: 'Deputy Group Leader',
    general_member: 'General Member'
};

const MEMBER_STATUSES = {
    active: { color: 'bg-green-500', label: 'Active' },
    probation: { color: 'bg-blue-500', label: 'Probation' },
    reserve: { color: 'bg-purple-500', label: 'Reserve' },
    inactive: { color: 'bg-red-500', label: 'Inactive' },
    applicant: { color: 'bg-amber-500', label: 'Applicant' },
    support: { color: 'bg-teal-500', label: 'Support' },
};

// Portal sections that can be controlled
const PORTAL_SECTIONS = [
    { key: 'calendar', label: 'Calendar', icon: Calendar },
    { key: 'forms', label: 'Forms', icon: FileText },
    { key: 'logbook', label: 'Logbook', icon: BookOpen },
    { key: 'training', label: 'Training', icon: GraduationCap },
    { key: 'members', label: 'Members Directory', icon: Users },
    { key: 'links', label: 'Links', icon: Link2 },
    { key: 'resources', label: 'Fleet, Facilities & Stores', icon: Truck },
];

export default function MemberProfilePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, hasUnitAccess, hasGroupAccess } = useAuth();
    const [member, setMember] = useState(null);
    const [groups, setGroups] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showLockDialog, setShowLockDialog] = useState(false);
    const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
    const [resetPasswordResult, setResetPasswordResult] = useState(null);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    const isOwnProfile = currentUser?.id === id;
    const canEdit = isOwnProfile || hasUnitAccess();
    const canManageUser = hasGroupAccess() && !isOwnProfile;
    const isNetworkManager = currentUser?.role === 'network_administrator' || currentUser?.role === 'network_manager';

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            const [memberRes, groupsRes] = await Promise.all([
                axios.get(`${API_URL}/users/${id}`),
                axios.get(`${API_URL}/groups`)
            ]);
            setMember(memberRes.data);
            setGroups(groupsRes.data);
            setFormData(memberRes.data);

            // Fetch attendance
            const attendanceRes = await axios.get(`${API_URL}/attendance`, { params: { user_id: id, limit: 10 } });
            setAttendance(attendanceRes.data);
        } catch (error) {
            console.error('Failed to fetch member:', error);
            toast.error('Failed to load member profile');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSubmitting(true);
        try {
            const updatePayload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone: formData.phone,
                emergency_contact_name: formData.emergency_contact_name,
                emergency_contact_phone: formData.emergency_contact_phone,
                group_id: formData.group_id,
                role: formData.role,
                member_status: formData.member_status,
                section_access: formData.section_access
            };
            
            // Only include username if network manager is editing
            if (isNetworkManager && formData.username) {
                updatePayload.username = formData.username;
            }
            
            await axios.put(`${API_URL}/users/${id}`, updatePayload);
            setMember({...formData});
            setEditing(false);
            toast.success('Profile updated');
            fetchData(); // Refresh data
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update profile');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLockToggle = async () => {
        const isLocked = member?.is_locked;
        try {
            if (isLocked) {
                await axios.post(`${API_URL}/users/${id}/unlock`);
                toast.success('Account unlocked');
            } else {
                await axios.post(`${API_URL}/users/${id}/lock`);
                toast.success('Account locked');
            }
            fetchData();
            setShowLockDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update account status');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_URL}/users/${id}`);
            toast.success('Account deleted');
            navigate('/admin/members');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete account');
        }
    };

    const handleResetPassword = async () => {
        setResettingPassword(true);
        try {
            const response = await axios.post(`${API_URL}/users/${id}/reset-password`);
            setResetPasswordResult(response.data);
            toast.success('Password reset successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
            setShowResetPasswordDialog(false);
        } finally {
            setResettingPassword(false);
        }
    };

    const copyToClipboard = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            toast.success('Copied to clipboard');
            setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
            toast.error('Failed to copy');
        }
    };

    const closeResetPasswordDialog = () => {
        setShowResetPasswordDialog(false);
        setResetPasswordResult(null);
    };

    const handleSectionAccessChange = (sectionKey, enabled) => {
        setFormData(prev => ({
            ...prev,
            section_access: {
                ...(prev.section_access || {}),
                [sectionKey]: enabled
            }
        }));
    };

    const getSectionAccess = (sectionKey) => {
        // Default to true if not set
        return formData.section_access?.[sectionKey] !== false;
    };

    const getGroupName = (groupId) => {
        const group = groups.find(g => g.id === groupId);
        return group?.name || 'No Group';
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calculate attendance stats
    const attendanceStats = {
        totalHours: attendance.reduce((sum, a) => sum + (a.duration_hours || 0), 0),
        thisMonth: attendance.filter(a => {
            const date = new Date(a.clock_in);
            const now = new Date();
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        }).reduce((sum, a) => sum + (a.duration_hours || 0), 0),
        dutyOfficerShifts: attendance.filter(a => a.is_duty_officer).length
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <header className="bg-primary text-white h-14 flex items-center px-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        className="text-white hover:bg-white/20 mr-2"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-lg font-semibold">Loading...</h1>
                </header>
                <div className="p-4 space-y-4">
                    <div className="h-48 bg-muted rounded-lg animate-pulse"></div>
                    <div className="h-32 bg-muted rounded-lg animate-pulse"></div>
                </div>
            </div>
        );
    }

    if (!member) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-6 text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                        <h2 className="text-xl font-semibold mb-2">Member Not Found</h2>
                        <Button onClick={() => navigate('/members')}>
                            Back to Members
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="member-profile-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="text-white hover:bg-white/20"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">Member Profile</h1>
                    </div>
                    {canEdit && !editing && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setEditing(true)}
                            data-testid="edit-profile-btn"
                        >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                        </Button>
                    )}
                    {editing && (
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setEditing(false);
                                    setFormData(member);
                                }}
                                className="text-white hover:bg-white/20"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleSave}
                                disabled={submitting}
                                data-testid="save-profile-btn"
                            >
                                <Save className="w-4 h-4 mr-1" />
                                Save
                            </Button>
                        </div>
                    )}
                </div>
            </header>

            {/* Profile Header Card */}
            <div className="p-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <Avatar className="w-20 h-20">
                                <AvatarImage src={member.profile_image} />
                                <AvatarFallback className="text-2xl bg-primary text-white">
                                    {member.first_name?.[0]}{member.last_name?.[0]}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                                {editing ? (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs">First Name</Label>
                                                <Input
                                                    value={formData.first_name || ''}
                                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                                    data-testid="edit-firstname"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Last Name</Label>
                                                <Input
                                                    value={formData.last_name || ''}
                                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                                    data-testid="edit-lastname"
                                                />
                                            </div>
                                        </div>
                                        {isNetworkManager && (
                                            <div>
                                                <Label className="text-xs">Username (SES ID)</Label>
                                                <Input
                                                    value={formData.username || ''}
                                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                                    placeholder="e.g. ADM-jsmith"
                                                    data-testid="edit-username"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-xl font-bold">
                                            {member.first_name} {member.last_name}
                                        </h2>
                                        <p className="text-muted-foreground">@{member.username}</p>
                                    </>
                                )}
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                                        <Shield className="w-3 h-3 mr-1" />
                                        {ROLE_LABELS[member.role] || member.role}
                                    </Badge>
                                    {member.member_status && MEMBER_STATUSES[member.member_status] && (
                                        <Badge className={`${MEMBER_STATUSES[member.member_status].color} text-white`}>
                                            {MEMBER_STATUSES[member.member_status].label}
                                        </Badge>
                                    )}
                                    {member.group_id && (
                                        <Badge variant="outline">
                                            <Users className="w-3 h-3 mr-1" />
                                            {getGroupName(member.group_id)}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
                <TabsList className="w-full">
                    <TabsTrigger value="profile" className="flex-1">Profile</TabsTrigger>
                    <TabsTrigger value="attendance" className="flex-1">Attendance</TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="space-y-4 mt-4">
                    {/* Contact Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Contact Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Email</Label>
                                    {editing ? (
                                        <Input
                                            type="email"
                                            value={formData.email || ''}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            data-testid="edit-email"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2 mt-1">
                                            <Mail className="w-4 h-4 text-muted-foreground" />
                                            <span>{member.email || '-'}</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Phone</Label>
                                    {editing ? (
                                        <Input
                                            value={formData.phone || ''}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            data-testid="edit-phone"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2 mt-1">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <span>{member.phone || '-'}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Emergency Contact */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base text-amber-600 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Emergency Contact
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Contact Name</Label>
                                    {editing ? (
                                        <Input
                                            value={formData.emergency_contact_name || ''}
                                            onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                                            placeholder="Emergency contact name"
                                            data-testid="edit-emergency-name"
                                        />
                                    ) : (
                                        <p className="mt-1">{member.emergency_contact_name || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Contact Phone</Label>
                                    {editing ? (
                                        <Input
                                            value={formData.emergency_contact_phone || ''}
                                            onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                                            placeholder="Emergency contact phone"
                                            data-testid="edit-emergency-phone"
                                        />
                                    ) : (
                                        <p className="mt-1">{member.emergency_contact_phone || '-'}</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Role & Group (Admin only) */}
                    {hasUnitAccess() && editing && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Role & Assignment</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Role</Label>
                                        <Select
                                            value={formData.role}
                                            onValueChange={(value) => setFormData({ ...formData, role: value })}
                                        >
                                            <SelectTrigger data-testid="edit-role">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                                                    <SelectItem key={value} value={value}>
                                                        {label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Group</Label>
                                        <Select
                                            value={formData.group_id || 'none'}
                                            onValueChange={(value) => setFormData({ ...formData, group_id: value === 'none' ? null : value })}
                                        >
                                            <SelectTrigger data-testid="edit-group">
                                                <SelectValue placeholder="Select group" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">No Group</SelectItem>
                                                {groups.map((group) => (
                                                    <SelectItem key={group.id} value={group.id}>
                                                        {group.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Member Status</Label>
                                    <Select
                                        value={formData.member_status || 'active'}
                                        onValueChange={(value) => setFormData({ ...formData, member_status: value })}
                                    >
                                        <SelectTrigger data-testid="edit-status">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(MEMBER_STATUSES).map(([value, { label }]) => (
                                                <SelectItem key={value} value={value}>
                                                    {label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Section Access Control (Admin only) */}
                    {canManageUser && editing && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Portal Section Access</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Control which sections this member can access on the portal.
                                </p>
                                <div className="space-y-3">
                                    {PORTAL_SECTIONS.map(({ key, label, icon: Icon }) => (
                                        <div key={key} className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-3">
                                                <Icon className="w-4 h-4 text-muted-foreground" />
                                                <span>{label}</span>
                                            </div>
                                            <Switch
                                                checked={getSectionAccess(key)}
                                                onCheckedChange={(checked) => handleSectionAccessChange(key, checked)}
                                                data-testid={`section-access-${key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Admin Actions (Group Admin+, not own profile) */}
                    {canManageUser && (
                        <Card className="border-destructive/30">
                            <CardHeader>
                                <CardTitle className="text-base text-destructive flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    Account Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Button
                                    variant="outline"
                                    className="w-full justify-start"
                                    onClick={() => setShowResetPasswordDialog(true)}
                                    data-testid="reset-password-btn"
                                >
                                    <KeyRound className="w-4 h-4 mr-2" />
                                    Reset Password
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full justify-start"
                                    onClick={() => setShowLockDialog(true)}
                                    data-testid="lock-account-btn"
                                >
                                    {member?.is_locked ? (
                                        <>
                                            <Unlock className="w-4 h-4 mr-2" />
                                            Unlock Account
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="w-4 h-4 mr-2" />
                                            Lock Account
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="destructive"
                                    className="w-full justify-start"
                                    onClick={() => setShowDeleteDialog(true)}
                                    data-testid="delete-account-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Account
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Account Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Account Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Member Since</p>
                                    <p className="font-medium">{formatDate(member.created_at)}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Last Active</p>
                                    <p className="font-medium">{formatDate(member.last_active || member.created_at)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Attendance Tab */}
                <TabsContent value="attendance" className="space-y-4 mt-4">
                    {/* Stats Summary */}
                    <div className="grid grid-cols-3 gap-3">
                        <Card>
                            <CardContent className="p-4 text-center">
                                <p className="text-2xl font-bold text-primary">
                                    {Math.round(attendanceStats.totalHours)}
                                </p>
                                <p className="text-xs text-muted-foreground">Total Hours</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <p className="text-2xl font-bold text-primary">
                                    {Math.round(attendanceStats.thisMonth)}
                                </p>
                                <p className="text-xs text-muted-foreground">This Month</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <p className="text-2xl font-bold text-purple-600">
                                    {attendanceStats.dutyOfficerShifts}
                                </p>
                                <p className="text-xs text-muted-foreground">DO Shifts</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recent Attendance */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Recent Attendance</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {attendance.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No attendance records</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {attendance.map((record) => (
                                        <div
                                            key={record.id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                                        >
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium">
                                                        {record.activity_name || 'General Attendance'}
                                                    </p>
                                                    {record.is_duty_officer && (
                                                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                                                            DO
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDateTime(record.clock_in)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                {record.duration_hours ? (
                                                    <p className="font-medium text-primary">
                                                        {record.duration_hours.toFixed(1)} hrs
                                                    </p>
                                                ) : (
                                                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                                                        Active
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Bottom Spacing */}
            <div className="h-8"></div>

            {/* Lock Account Dialog */}
            <AlertDialog open={showLockDialog} onOpenChange={setShowLockDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {member?.is_locked ? 'Unlock Account?' : 'Lock Account?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {member?.is_locked 
                                ? `This will restore ${member?.first_name}'s access to the system.`
                                : `This will prevent ${member?.first_name} from logging in until the account is unlocked.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleLockToggle}
                            className={member?.is_locked ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
                            data-testid="confirm-lock-btn"
                        >
                            {member?.is_locked ? 'Unlock' : 'Lock'} Account
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Account Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete {member?.first_name} {member?.last_name}'s account 
                            and all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-btn"
                        >
                            Delete Account
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reset Password Dialog */}
            <Dialog open={showResetPasswordDialog} onOpenChange={closeResetPasswordDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5" />
                            Reset Password
                        </DialogTitle>
                        <DialogDescription>
                            {!resetPasswordResult 
                                ? `Reset the password for ${member?.first_name} ${member?.last_name}. They will be required to change it on their next login.`
                                : 'Password has been reset. Share the temporary password with the user.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {!resetPasswordResult ? (
                        <div className="py-4">
                            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div className="text-sm text-amber-700 dark:text-amber-300">
                                    <p className="font-medium">This will:</p>
                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                        <li>Generate a new temporary password</li>
                                        <li>Invalidate their current password immediately</li>
                                        <li>Require them to set a new password on next login</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 py-4">
                            {/* Temporary Password */}
                            <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-green-700 dark:text-green-300">Temporary Password</p>
                                        <p className="text-2xl font-mono font-bold text-green-800 dark:text-green-200 mt-1">
                                            {resetPasswordResult.temporary_password}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(resetPasswordResult.temporary_password, 'password')}
                                        className="shrink-0"
                                        data-testid="copy-password-btn"
                                    >
                                        {copiedField === 'password' ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* Email Template */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm font-medium">Password Reset Email</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(resetPasswordResult.reset_email, 'email')}
                                        data-testid="copy-email-btn"
                                    >
                                        {copiedField === 'email' ? (
                                            <>
                                                <Check className="w-4 h-4 mr-1 text-green-600" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-4 h-4 mr-1" />
                                                Copy Email
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <div className="p-3 bg-muted rounded-lg max-h-48 overflow-y-auto">
                                    <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
                                        {resetPasswordResult.reset_email}
                                    </pre>
                                </div>
                            </div>

                            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                <p className="text-sm text-blue-700 dark:text-blue-300 break-words">
                                    Copy and send this email to <strong className="break-all">{resetPasswordResult.user.email || member?.email || 'the user'}</strong> to notify them of their new password.
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {!resetPasswordResult ? (
                            <>
                                <Button variant="outline" onClick={closeResetPasswordDialog} disabled={resettingPassword}>
                                    Cancel
                                </Button>
                                <Button onClick={handleResetPassword} disabled={resettingPassword} data-testid="confirm-reset-btn">
                                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={closeResetPasswordDialog} data-testid="close-reset-dialog-btn">
                                Done
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
